import {
  GridColDef,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { getRouteApi } from '@tanstack/react-router';
import { useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useQueryAccountingReferencesDetails,
  useQuerySitesDetails,
  useQueryUserAnalyticsDetails,
} from '~/data/analytics';

import { getFormattedNumber, toSnakeCase } from '@vestigas/utils';
import { currentLocale, i18n } from '~/utils';

import { RowModel, USAGE_TABS } from '../constants';
import { useDateRange } from '../utils';

import { ActiveStateIndicator } from './ActiveStateIndicator';

const locale = currentLocale();

export const costCentersColumns = (): GridColDef[] => [
  {
    field: 'accrefName',
    flex: 1,
    headerName: i18n.t(($) => $.usage.detailsColumn.name, { ns: 'analytics' }),
    minWidth: 240,
    resizable: true,
    sortable: true,
  },
  {
    align: 'center',
    field: 'isActive',
    headerAlign: 'center',
    headerName: i18n.t(($) => $.usage.detailsColumn.isActive, {
      ns: 'analytics',
    }),
    minWidth: 56,
    renderCell: ({ value }) => <ActiveStateIndicator isActive={value} />,
    resizable: false,
    sortable: true,
  },
  {
    align: 'right',
    field: 'unsignedDeliveryNotesCount',
    headerName: i18n.t(
      ($) => $.usage.detailsColumn.unsignedDeliveryNotesCount,
      {
        ns: 'analytics',
      },
    ),
    minWidth: 200,
    resizable: true,
    sortable: true,
    valueFormatter: (value) => {
      return getFormattedNumber({ value, digits: 0 });
    },
  },
  {
    align: 'right',
    field: 'signedDeliveryNotesCount',
    headerName: i18n.t(($) => $.usage.detailsColumn.signedDeliveryNotesCount, {
      ns: 'analytics',
    }),
    minWidth: 180,
    resizable: true,
    sortable: true,
    valueFormatter: (value) => {
      return getFormattedNumber({ value, digits: 0 });
    },
  },
  {
    align: 'right',
    field: 'assignedDeliveryNotesCount',
    headerName: i18n.t(
      ($) => $.usage.detailsColumn.assignedDeliveryNotesCount,
      {
        ns: 'analytics',
      },
    ),
    minWidth: 210,
    resizable: true,
    sortable: true,
    valueFormatter: (value) => {
      return getFormattedNumber({ value, digits: 0 });
    },
  },
  {
    align: 'right',
    field: 'signatureRate',
    headerName: i18n.t(($) => $.usage.detailsColumn.signatureRate, {
      ns: 'analytics',
    }),
    minWidth: 130,
    resizable: true,
    sortable: true,
    valueFormatter: (value) => {
      return [getFormattedNumber({ value, digits: 0 }), '%'].join(' ');
    },
  },
];

export const sitesColumns = (): GridColDef[] => [
  {
    field: 'siteName',
    flex: 1,
    headerName: i18n.t(($) => $.usage.detailsColumn.name, { ns: 'analytics' }),
    minWidth: 240,
    resizable: true,
    sortable: true,
  },
  {
    align: 'center',
    field: 'isActive',
    headerAlign: 'center',
    headerName: i18n.t(($) => $.usage.detailsColumn.isActive, {
      ns: 'analytics',
    }),
    minWidth: 56,
    renderCell: ({ value }) => <ActiveStateIndicator isActive={value} />,
    resizable: false,
    sortable: true,
  },
  {
    align: 'right',
    field: 'unsignedDeliveryNotesCount',
    headerName: i18n.t(
      ($) => $.usage.detailsColumn.unsignedDeliveryNotesCount,
      {
        ns: 'analytics',
      },
    ),
    minWidth: 200,
    resizable: true,
    sortable: true,
    valueFormatter: (value) => {
      return getFormattedNumber({ value, digits: 0 });
    },
  },
  {
    align: 'right',
    field: 'signedDeliveryNotesCount',
    headerName: i18n.t(($) => $.usage.detailsColumn.signedDeliveryNotesCount, {
      ns: 'analytics',
    }),
    minWidth: 180,
    resizable: true,
    sortable: true,
    valueFormatter: (value) => {
      return getFormattedNumber({ value, digits: 0 });
    },
  },
  {
    align: 'right',
    field: 'assignedDeliveryNotesCount',
    headerName: i18n.t(
      ($) => $.usage.detailsColumn.assignedDeliveryNotesCount,
      {
        ns: 'analytics',
      },
    ),
    minWidth: 210,
    resizable: true,
    sortable: true,
    valueFormatter: (value) => {
      return getFormattedNumber({ value, digits: 0 });
    },
  },
  {
    align: 'right',
    field: 'signatureRate',
    headerName: i18n.t(($) => $.usage.detailsColumn.signatureRate, {
      ns: 'analytics',
    }),
    minWidth: 130,
    resizable: true,
    sortable: true,
    valueFormatter: (value) => {
      return [getFormattedNumber({ value, digits: 0 }), '%'].join(' ');
    },
  },
];

export const usersColumns = (): GridColDef[] => [
  {
    field: 'firstName',
    flex: 1,
    headerName: i18n.t(($) => $.usage.detailsColumn.firstName, {
      ns: 'analytics',
    }),
    minWidth: 100,
    resizable: true,
    sortable: true,
  },
  {
    field: 'lastName',
    flex: 1,
    headerName: i18n.t(($) => $.usage.detailsColumn.lastName, {
      ns: 'analytics',
    }),
    minWidth: 100,
    resizable: true,
    sortable: true,
  },
  {
    field: 'email',
    flex: 1,
    headerName: i18n.t(($) => $.usage.detailsColumn.email, {
      ns: 'analytics',
    }),
    minWidth: 160,
    resizable: true,
    sortable: true,
  },
  {
    field: 'companyName',
    flex: 1,
    headerName: i18n.t(($) => $.usage.detailsColumn.companyName, {
      ns: 'analytics',
    }),
    minWidth: 160,
    resizable: true,
    sortable: true,
  },
  {
    align: 'center',
    field: 'isActive',
    headerAlign: 'center',
    headerName: i18n.t(($) => $.usage.detailsColumn.isActive, {
      ns: 'analytics',
    }),
    minWidth: 56,
    renderCell: ({ value }) => <ActiveStateIndicator isActive={value} />,
    resizable: false,
    sortable: true,
  },
  {
    align: 'right',
    field: 'signedDeliveryNotesCount',
    headerName: i18n.t(($) => $.usage.detailsColumn.signedDeliveryNotesCount, {
      ns: 'analytics',
    }),
    minWidth: 180,
    resizable: true,
    sortable: true,
    valueFormatter: (value) => {
      return getFormattedNumber({ value, digits: 0 });
    },
  },
  {
    align: 'right',
    field: 'lastActiveDatetime',
    headerName: i18n.t(($) => $.usage.detailsColumn.lastActiveDatetime, {
      ns: 'analytics',
    }),
    minWidth: 110,
    resizable: true,
    sortable: true,
    type: 'date',
    valueFormatter: (value) => {
      return value
        ? Intl.DateTimeFormat(locale, {
            dateStyle: 'medium',
          }).format(new Date(value))
        : undefined;
    },
    valueGetter: (value) => {
      if (!value) {
        return undefined;
      }

      return new Date(value);
    },
  },
  {
    align: 'right',
    field: 'mobileAppVersion',
    headerName: i18n.t(($) => $.usage.detailsColumn.mobileAppVersion, {
      ns: 'analytics',
    }),
    minWidth: 160,
    resizable: true,
    sortable: true,
  },
];

export function useUsageDetailsTable() {
  const { t } = useTranslation('analytics');

  const AnalyticsUsageRoute = getRouteApi('/(main)/analytics/usage/');

  const navigate = AnalyticsUsageRoute.useNavigate();

  const { detailsTable, page, searchString, tableSortBy, tableSortOrder } =
    AnalyticsUsageRoute.useSearch({
      select: ({
        detailsTable,
        page,
        searchString,
        tableSortBy,
        tableSortOrder,
      }) => ({
        detailsTable,
        page,
        searchString,
        tableSortBy,
        tableSortOrder,
      }),
    });

  const dateRange = useDateRange();

  function onChangeDetailsTab(detailsTable: (typeof USAGE_TABS)[number]) {
    navigate({
      search: (previous) => ({
        ...previous,
        detailsTable,
        page: 0, // Reset table page when switching details table
        searchString: '', // Reset search string when switching details table
        tableSortBy: undefined, // Reset sorting when switching details table
        tableSortOrder: undefined, // Reset sorting when switching details table
      }),
    });
  }

  function onSearch(searchString: string) {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 0, // Reset table page when changing search string
        searchString,
      }),
    });
  }

  function onChangePage(page: number) {
    navigate({
      search: (previous) => ({
        ...previous,
        page,
      }),
    });
  }

  function onChangeSortModel(sortModel: GridSortModel) {
    if (!sortModel?.length) {
      // Empty sortModel means clear sorting
      // Only update state if there's currently sorting applied
      if (tableSortBy || tableSortOrder) {
        navigate({
          search: (previous) => ({
            ...previous,
            tableSortBy: undefined,
            tableSortOrder: undefined,
          }),
        });
      }
      return;
    }

    const field = sortModel[0]?.field;
    const sort = sortModel[0]?.sort;

    // Only update state if we have both field and sort direction
    if (!field || !sort) {
      return;
    }

    const newSortBy = toSnakeCase(field);
    const newSortOrder = sort.toUpperCase() as 'ASC' | 'DESC';

    // Only update state if the sort has actually changed
    if (newSortBy === tableSortBy && newSortOrder === tableSortOrder) {
      return;
    }

    navigate({
      search: (previous) => ({
        ...previous,
        tableSortBy: newSortBy,
        tableSortOrder: newSortOrder,
      }),
    });
  }

  const costCenterDetails = useQueryAccountingReferencesDetails(
    {
      data: detailsTable,
      dateRange,
      page,
      searchString,
      sortBy: tableSortBy,
      sortOrder: tableSortOrder,
    },
    {
      enabled: detailsTable === 'costCenters',
    },
  );

  const siteDetails = useQuerySitesDetails(
    {
      data: detailsTable,
      dateRange,
      page,
      searchString,
      sortBy: tableSortBy,
      sortOrder: tableSortOrder,
    },
    {
      enabled: detailsTable === 'sites',
    },
  );

  const userDetails = useQueryUserAnalyticsDetails(
    {
      data: detailsTable,
      dateRange,
      page,
      searchString,
      sortBy: tableSortBy,
      sortOrder: tableSortOrder,
    },
    {
      enabled: detailsTable === 'users',
    },
  );

  const apiRef = useGridApiRef();

  const searchInputPlaceholder = useMemo(() => {
    switch (detailsTable) {
      case 'costCenters':
        return t(
          ($) => $.usage.usageDetails.searchInputCostCenters.placeholder,
        );

      case 'sites':
        return t(($) => $.usage.usageDetails.searchInputSites.placeholder);

      case 'users':
        return t(($) => $.usage.usageDetails.searchInputUsers.placeholder);

      default:
        return t(($) => $.usage.usageDetails.searchInput.placeholder);
    }
  }, [detailsTable, t]);

  const getRowId = useMemo(() => {
    return (row: RowModel) => {
      switch (detailsTable) {
        case 'costCenters':
          return row.accrefId;

        case 'sites':
          return row.siteId;

        case 'users':
          return row.userId;
      }
    };
  }, [detailsTable]);

  const usageDetails = useMemo(() => {
    switch (detailsTable) {
      case 'costCenters':
        return costCenterDetails;

      case 'sites':
        return siteDetails;

      case 'users':
        return userDetails;
    }
  }, [costCenterDetails, detailsTable, siteDetails, userDetails]);

  const columns = useMemo(() => {
    switch (detailsTable) {
      case 'costCenters':
        return costCentersColumns();

      case 'sites':
        return sitesColumns();

      case 'users':
        return usersColumns();
    }
  }, [detailsTable]);

  useLayoutEffect(() => {
    if (
      Boolean(apiRef.current) &&
      Boolean(usageDetails?.isSuccess) &&
      detailsTable === 'users'
    ) {
      // Autosize columns of users table when its data is loaded to achieve the best fit of the data
      const timeoutId = setTimeout(() => {
        apiRef.current.autosizeColumns({
          expand: true,
          includeHeaders: true,
          includeOutliers: true,
        });
      }, 50); // Wait for the DOM to be updated before triggering the autosize

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [apiRef, detailsTable, usageDetails?.isSuccess]);

  return {
    apiRef,
    columns,
    dateRange,
    detailsTable,
    getRowId,
    onChangeDetailsTab,
    onChangePage,
    onChangeSortModel,
    onSearch,
    page,
    searchInputPlaceholder,
    searchString,
    t,
    tableSortBy,
    tableSortOrder,
    usageDetails,
  };
}
